import { buttonUrl, isButton } from '@bw/apis/contentful';
import { cn } from '@bw/core';
import Link from "next/link";
import { BlockType } from '../../../factories/types';
import { annotate } from '../../../utils/annotate';
import { defined } from '../../../utils/defined';
import { ButtonWrapper } from '../../button/ButtonWrapper';
import { HeaderNavItem, isMenu } from '../types';
import styles from "./header-mobile-menu.module.scss";

interface MobileMenuProps {
  menuContent: HeaderNavItem[]
  ctaSection: JSX.Element
  activeHeaderItem: HeaderNavItem | null
  onItemSelected: (menuItem: HeaderNavItem) => void
  onBackClick: () => void
  onCloseClick: () => void,
}

export const MobileMenu: React.FC<MobileMenuProps> = (
  { menuContent, ctaSection, activeHeaderItem, onItemSelected, onBackClick, onCloseClick }
) =>
  <div className={styles.megaMenuContentsContainer}>
    <div className={styles.menuMobileHeading}>
      <h5>Menu</h5>
    </div>

    <ul className={styles.menuMobileContentWrapper}>
      {menuContent.map((headerItem: HeaderNavItem, index: number) => (
        <li
          key={index}
          className={styles.subMenuMobileContent}
          data-menu-index={index}
        >
          {isMenu(headerItem) ? (
            <>
              <span
                className={styles.menuItemDropdown}
                tabIndex={activeHeaderItem ? -1 : 0}
                onClick={() => onItemSelected(headerItem)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onItemSelected(headerItem);
                  }
                }}
                {...annotate(headerItem, 'title')}
              >
                {headerItem.fields.title}
                <ChevronRight />
              </span>
              <div
                tabIndex={headerItem === activeHeaderItem ? 0 : -1}
                className={cn(
                  styles.subMenuMobileContentWrapper,
                  headerItem === activeHeaderItem && styles['subMenuMobileContentWrapper--isActive'],
                )}
              >
                <div className={styles.menuMobileHeading}>
                  <button className={styles.backBtn} onClick={onBackClick}>
                    <ChevronLeft />
                    Back
                  </button>
                  <h5>{headerItem.fields.title}</h5>
                </div>

                <ul className={styles.subMenuList}>
                  {defined(headerItem.fields.menuItems).map(
                    (item, num) => (
                      <li className={styles.subMenuMobileContent} key={num} {...annotate(item, 'title')}>
                        <span className={cn(styles.menuItemLink, styles['menu-list-mobile'])}>
                          {
                            isButton(item) && <ButtonWrapper data={item} onClick={onCloseClick} tabIndex={activeHeaderItem === item ? 0 : -1} buttonStyle='link' />
                          }
                        </span>
                      </li>
                    )
                  )}
                </ul>
                {headerItem.fields.actions &&
                  <div className={styles.subMenuItemFooter}>
                    {defined(headerItem.fields.actions).map((action, index) =>
                      <Link
                        href={buttonUrl(action)}
                        key={index}
                        onClick={onCloseClick}
                        {...annotate(action, 'title')}
                      >
                        {action.fields.title}
                      </Link>
                    )}
                  </div>
                }
                <div className={styles.menuActionButtonsContainer}>
                  {ctaSection}
                </div>
              </div>
            </>
          ) : isButton(headerItem) && (
              <span className={cn(styles.menuItemLink, styles['menu-list-mobile'])}>
                <ButtonWrapper data={headerItem as BlockType<'button'>} onClick={onCloseClick} tabIndex={activeHeaderItem === headerItem ? 0 : -1} buttonStyle='link' />
              </span>
            )}
        </li>
      ))}
    </ul>
    <div className={styles.menuActionButtonsContainer}>
      {ctaSection}
    </div>
  </div>

const ChevronLeft = () =>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
  >
    <path
      d="M10 4L6 8L10 12"
      stroke="currentColor"
      style={{
        stroke: 'currentcolor',
        strokeOpacity: 1,
        strokeWidth: 1.33333,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
      }}
    />
  </svg>

const ChevronRight = () =>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
  >
    <path
      d="M6 12L10 8L6 4"
      stroke="currentColor"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
